import styles from './index.module.css'

import React, { useEffect, useState } from 'react'

import { formatRelativeDate } from '@disco/utils/time-utils'

import type { TrackId } from 'types/track'

import { LuLockKeyhole } from '@react-icons/all-files/lu/LuLockKeyhole'

import { Flex, Skeleton, Text, Tooltip } from 'components/ds'

import { PlaylistThumbnail } from './playlist-thumbnail'
import {
  type AppearsOnPlaylist,
  getCachedTrackPlaylists,
  loadTrackPlaylists,
} from './playlists-loader'

const HIDDEN_PLAYLISTS_TOOLTIP =
  'Other users in your DISCO might set a playlist as "Viewable only by me". This track is in one or more of those playlists. Keep that in mind when editing or deleting the track.'

const ownerName = (playlist: AppearsOnPlaylist): string =>
  [playlist.owner?.first_name, playlist.owner?.last_name]
    .filter(Boolean)
    .join(' ')

const MAX_SKELETON_ROWS = 5

const PlaylistRowSkeleton = () => (
  <Flex align="center" gap="2">
    <Skeleton width="32px" height="32px" className={styles.artwork} />
    <Flex direction="column" gap="1" className={styles.playlistInfo}>
      <Skeleton width="70%" height="var(--font-size-2)" />
      <Skeleton width="40%" height="var(--font-size-1)" />
    </Flex>
  </Flex>
)

type Props = {
  trackId: TrackId
  count: number
}

export const AppearsOnPlaylistsCard = ({ trackId, count }: Props) => {
  const [playlists, setPlaylists] = useState<AppearsOnPlaylist[] | null>(
    () => getCachedTrackPlaylists(trackId) ?? null,
  )
  const [hasError, setHasError] = useState(false)

  // The count is business-wide while the list is scoped to the viewer's
  // groups, so playlists the viewer can't access show up as a remainder.
  const hiddenCount = playlists ? Math.max(0, count - playlists.length) : 0

  useEffect(() => {
    let cancelled = false
    loadTrackPlaylists(trackId)
      .then(result => {
        if (!cancelled) setPlaylists(result)
      })
      .catch(() => {
        if (!cancelled) setHasError(true)
      })

    return () => {
      cancelled = true
    }
  }, [trackId])

  return (
    <Flex direction="column" gap="2">
      <Text size="1" weight="bold" color="gray">
        {`Appears on ${count} ${count === 1 ? 'playlist' : 'playlists'}`}
      </Text>

      {hasError ? (
        <Text size="1" color="gray">
          Couldn&apos;t load playlists
        </Text>
      ) : playlists === null ? (
        <Flex
          direction="column"
          className={styles.playlistList}
          data-testid="appears-on-playlists-loading"
        >
          {Array.from({ length: Math.min(count, MAX_SKELETON_ROWS) }).map(
            (_, index) => (
              <PlaylistRowSkeleton key={index} />
            ),
          )}
        </Flex>
      ) : (
        <Flex direction="column" className={styles.playlistList}>
          {playlists.map(playlist => {
            const owner = ownerName(playlist)
            const date = formatRelativeDate(playlist.timestamp)
            return (
              <Flex
                key={playlist.id}
                align="center"
                gap="2"
                data-testid="appears-on-playlist-row"
              >
                <PlaylistThumbnail
                  imageUrl={playlist.image_url}
                  artworks={playlist.tracks_artworks}
                  name={playlist.name}
                />
                <Flex direction="column" className={styles.playlistInfo}>
                  <Text
                    size="2"
                    weight="medium"
                    className={styles.playlistName}
                  >
                    {playlist.name}
                  </Text>
                  <Flex align="center" gap="1">
                    {owner && (
                      <Text size="1" color="gray">
                        {owner}
                      </Text>
                    )}
                    {owner && date && (
                      <Text size="1" color="gray">
                        ·
                      </Text>
                    )}
                    {date && (
                      <Text size="1" color="gray">
                        {date}
                      </Text>
                    )}
                  </Flex>
                </Flex>
              </Flex>
            )
          })}
          {hiddenCount > 0 && (
            <Tooltip content={HIDDEN_PLAYLISTS_TOOLTIP} maxWidth="250px">
              <Flex
                align="center"
                gap="1"
                data-testid="appears-on-hidden-count"
              >
                <LuLockKeyhole size={12} />
                <Text size="1" color="gray" className={styles.hiddenCountText}>
                  {`${hiddenCount} more ${
                    hiddenCount === 1 ? 'playlist' : 'playlists'
                  } not visible to you`}
                </Text>
              </Flex>
            </Tooltip>
          )}
        </Flex>
      )}
    </Flex>
  )
}
